import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppHeader } from '../components/AppHeader';
import { Card, CardContent } from '../components/ui/card';
import { FideImportCard } from '../components/fide/FideImportCard';
import { getFideListStatus } from '../lib/api';
import type { FideListStatus } from '@swiss-magic/shared-types';

// Story 098 — dedicated org-admin page for managing the local FIDE rating-list
// mirror that powers FIDE ID autofill. Separate from director management: this
// is a bulk data-maintenance surface (upload the monthly dump; the cron also
// refreshes it), not a per-director action.
export function AdminFideListPage() {
  const { t, i18n } = useTranslation('director');
  const [status, setStatus] = useState<FideListStatus | null>(null);

  const loadStatus = useCallback(() => {
    getFideListStatus()
      .then(setStatus)
      .catch(() => setStatus(null));
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const lastUpdated = status?.lastUpdatedAt
    ? new Date(status.lastUpdatedAt).toLocaleDateString(i18n.language)
    : null;

  return (
    <div data-testid="admin-fide-list-page" className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-4xl px-6 py-8">
        <h1 className="mb-1 text-2xl font-semibold">
          {t('admin.fide_list.title')}
        </h1>
        <p className="mb-6 text-sm text-muted-foreground">
          {t('admin.fide_list.subtitle')}
        </p>

        <Card className="mb-6">
          <CardContent className="py-6">
            <h2 className="mb-2 text-lg font-semibold">
              {t('admin.fide_list.status_heading')}
            </h2>
            {status ? (
              <p data-testid="fide-list-status" className="text-sm">
                {t('admin.fide_list.status_count', { count: status.count })}
                {lastUpdated
                  ? ' · ' +
                    t('admin.fide_list.status_last_updated', { date: lastUpdated })
                  : ' · ' + t('admin.fide_list.status_never')}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('admin.fide_list.status_loading')}
              </p>
            )}
          </CardContent>
        </Card>

        <h2 className="mb-2 text-lg font-semibold">
          {t('admin.fide_import.title')}
        </h2>
        <FideImportCard onImported={loadStatus} />
        <p className="mt-3 text-xs text-muted-foreground">
          {t('admin.fide_list.cron_note')}
        </p>
      </main>
    </div>
  );
}
